import { Span } from "@opentelemetry/api";
import { otperformance } from "@opentelemetry/core";
import { hasKey, PerformanceEntries, PerformanceLegacy } from "@opentelemetry/sdk-trace-web";

import CONV from "../../semantic-conventions";
import { getSize } from "../../utils";
import { PerformanceTimingNames as PTN } from "./types";

/**
 * Helper function for starting an event on span based on {@link PerformanceEntries}
 * @param span
 * @param performanceName name of performance entry for time start
 * @param entries
 * @param ignoreZeros
 */
export function addSpanNetworkEvent(
  span: Span,
  performanceName: string,
  entries: PerformanceEntries,
  ignoreZeros = true,
): Span | undefined {
  if (
    hasKey(entries, performanceName) &&
    typeof entries[performanceName] === "number" &&
    !(ignoreZeros && entries[performanceName] === 0)
  ) {
    return span.addEvent(performanceName, entries[performanceName]);
  }

  return undefined;
}

/**
 * Helper function for adding network events and content length attributes.
 */
export function addSpanNetworkEvents(
  span: Span,
  resource: PerformanceEntries,
  ignoreNetworkEvents = false,
  ignoreZeros?: boolean,
  skipOldSemconvContentLengthAttrs?: boolean,
): void {
  if (ignoreZeros === undefined) {
    ignoreZeros = (resource as PerformanceNavigationTiming)[PTN.START_TIME] !== 0;
  }

  if (!ignoreNetworkEvents) {
    addSpanNetworkEvent(span, PTN.FETCH_START, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.DOMAIN_LOOKUP_START, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.DOMAIN_LOOKUP_END, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.CONNECT_START, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.SECURE_CONNECTION_START, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.CONNECT_END, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.REQUEST_START, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.RESPONSE_START, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.RESPONSE_END, resource, ignoreZeros);
  }

  if (!skipOldSemconvContentLengthAttrs) {
    // This block adds content-length-related span attributes using the
    // *old* HTTP semconv (v1.7.0).
    const encodedLength = resource[PTN.ENCODED_BODY_SIZE];
    if (encodedLength !== undefined) {
      span.setAttribute(CONV.HTTP_RESPONSE_CONTENT_LENGTH, encodedLength);
      span.setAttribute(CONV.SIZE, getSize(encodedLength));
    }

    const decodedLength = resource[PTN.DECODED_BODY_SIZE];
    // Spec: Not set if transport encoding not used (in which case encoded and decoded sizes match)
    if (decodedLength !== undefined && encodedLength !== decodedLength) {
      span.setAttribute(CONV.HTTP_RESPONSE_CONTENT_LENGTH_UNCOMPRESSED, decodedLength);
    }
  }
}

export const getPerformanceNavigationEntries = (): PerformanceEntries => {
  const entries: PerformanceEntries = {};
  const performanceNavigationTiming = (otperformance as unknown as Performance).getEntriesByType?.(
    "navigation",
  )[0] as PerformanceEntries;

  if (performanceNavigationTiming) {
    const keys = Object.values(PTN);
    keys.forEach((key: string) => {
      if (hasKey(performanceNavigationTiming, key)) {
        const value = performanceNavigationTiming[key];
        if (typeof value === "number") {
          entries[key] = value;
        }
      }
    });
  } else {
    // // fallback to previous version
    const perf: typeof otperformance & PerformanceLegacy = otperformance;
    const performanceTiming = perf.timing;
    if (performanceTiming) {
      const keys = Object.values(PTN);
      keys.forEach((key: string) => {
        if (hasKey(performanceTiming, key)) {
          const value = performanceTiming[key];
          if (typeof value === "number") {
            entries[key] = value;
          }
        }
      });
    }
  }

  return entries;
};

const performancePaintNames = {
  "first-paint": "firstPaint",
  "first-contentful-paint": "firstContentfulPaint",
};

export const addSpanPerformancePaintEvents = (span: Span) => {
  const performancePaintTiming = (otperformance as unknown as Performance).getEntriesByType?.(
    "paint",
  );
  if (performancePaintTiming) {
    performancePaintTiming.forEach(({ name, startTime }) => {
      if (hasKey(performancePaintNames, name)) {
        span.addEvent(performancePaintNames[name], startTime);
      }
    });
  }
};
